/**
 * MS-533/MS-605 — the payment page's invoice rows.
 *
 * Every figure below is the welcome book's, printed verbatim. Nothing here divides, sums or
 * re-derives: WelcomeBookDto already carries the split (Subtotal / VatAmount / VatRatePercent /
 * GuestTotal / NightlyRate), struck by the same resolver that draws the payment request email,
 * so the two surfaces state one arithmetic for one booking.
 *
 * MS-605 is what this replaces. The page used to divide the VAT-INCLUSIVE total by the night
 * count and print the result as the nightly rate, with no VAT row at all — a 500 + 75 booking
 * read as «575 ريال × 1 ليالٍ» on the screen the guest pays from, one click after an email that
 * correctly showed 500 and 75.
 */

const RIYAL = "﷼";

const AMOUNT = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Two decimals, then the symbol after one space — the same shape the invoice email prints. */
const money = (value: number): string => `${AMOUNT.format(value)} ${RIYAL}`;

/** The rate label carries the real rate, so 15 reads "15" and 5.5 reads "5.5". */
const RATE = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

/**
 * Arabic counts nights in four shapes, and "1 ليالٍ" was the tell that this page counted in none
 * of them: singular, dual, the 3–10 plural, then the 11-and-up singular again.
 */
const nightsPhrase = (nights: number): string => {
  if (nights === 1) return "ليلة واحدة";
  if (nights === 2) return "ليلتان";
  if (nights <= 10) return `${nights} ليالٍ`;
  return `${nights} ليلة`;
};

export interface InvoiceInput {
  /** Display only, and null when the published stay spans more than this booking. */
  nightlyRate?: number | null;
  numberOfNights?: number | null;
  /** The accommodation row's authoritative amount — never nightlyRate x numberOfNights. */
  subtotal?: number | null;
  /** 0 is a real answer: a booking that was charged no tax. The row is then omitted. */
  vatAmount?: number | null;
  vatRatePercent?: number | null;
  guestTotal?: number | null;
  totalPrice?: number | null;
}

export interface InvoiceRow {
  label: string;
  amount: string;
}

export interface InvoiceRows {
  nightly: InvoiceRow | null;
  vat: InvoiceRow | null;
  total: string;
}

export function buildInvoice({
  nightlyRate,
  numberOfNights,
  subtotal,
  vatAmount,
  vatRatePercent,
  guestTotal,
  totalPrice,
}: InvoiceInput): InvoiceRows {
  // GuestTotal is TotalPrice by construction (see the DTO); the fallback is for an API that
  // predates the split, not for a disagreement between the two.
  const total = money(guestTotal ?? totalPrice ?? 0);

  const nights = numberOfNights ?? 0;
  const nightly =
    nightlyRate != null && nights > 0
      ? {
          label: `${money(nightlyRate)} × ${nightsPhrase(nights)}`,
          amount: money(subtotal ?? guestTotal ?? totalPrice ?? 0),
        }
      : null;

  // Omitted rather than zeroed: an invoice must not assert a tax that was never charged. The
  // rate is dropped from the label, not the row, when the backend states an amount without one —
  // the guest can still add the rows up, which is the point of showing them.
  const vat =
    vatAmount != null && vatAmount > 0
      ? {
          label:
            vatRatePercent != null && vatRatePercent > 0
              ? `ضريبة القيمة المضافة (${RATE.format(vatRatePercent)}%)`
              : "ضريبة القيمة المضافة",
          amount: money(vatAmount),
        }
      : null;

  return { nightly, vat, total };
}
